"use client";

import type React from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Trash } from "lucide-react";

import { toast } from "@/utils/customToast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";

interface DeleteTemplateDialogProps {
  templateId: string;
  templateName: string;
  onSuccess?: () => void;
  children?: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
}

export function DeleteTemplateDialog({
  templateId,
  templateName,
  onSuccess,
  children,
  isOpen,
  onClose,
}: DeleteTemplateDialogProps) {
  const { t } = useTranslation(["common", "calendar"]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use either controlled or uncontrolled open state
  const dialogOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const handleOpenChange = (open: boolean) => {
    if (onClose && !open) {
      onClose();
    } else {
      setInternalIsOpen(open);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosApiInstance.delete(`${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}`);

      toast.success(t("calendar:templates.deleteSuccess"), {
        description: t("calendar:templates.deleteSuccessDescription"),
      });

      handleOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Failed to delete template:", error);
      toast.error(t("common:error"), {
        description: t("common:errors.genericErrorTitle"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {children && <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-accent-foreground">
            {t("calendar:templates.deleteTemplateTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("calendar:templates.deleteTemplateDescription")}
            <div className="bg-foreground mt-4 rounded-md p-4">
              <p className="text-accent-foreground font-medium">{templateName}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{t("common:cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive/50 hover:bg-red-500/30 focus:ring-red-600"
            disabled={isDeleting}
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
          >
            {isDeleting ? (
              <div className="flex items-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  className="mr-2"
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                >
                  <Icon height={"1rem"} icon="tabler:loader-2" width={"1rem"} />
                </motion.div>
                {t("calendar:templates.deleting")}
              </div>
            ) : (
              <>
                <Trash className="mr-2 h-4 w-4" />
                {t("calendar:templates.confirmDelete")}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
